import { ALLOWANCES, FREE_PLAN, PLANS, type PlanAllowances } from '@famanage/schemas';
import { Icon } from '@iconify/react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { LegalLinks } from './Legal';
import { track } from './analytics';
import { getRole } from './api';
import logoFull from './assets/brand/logo-full.svg';
import markOrange from './assets/brand/mark-orange.svg';
import ownerLiveHome from './assets/landing/owner-live-home.png';
import ownerMemory from './assets/landing/owner-memory.png';
import ownerMoney from './assets/landing/owner-money.png';
import ownerTasks from './assets/landing/owner-tasks.png';
import staffToday from './assets/landing/staff-today.png';
import { t } from './i18n';

/** The eight places the app can send you. */
export type BrandIconName =
  | 'home'
  | 'tasks'
  | 'quick'
  | 'staff'
  | 'routines'
  | 'money'
  | 'memory'
  | 'profile';

/**
 * The public landing page — engineered as one emotional arc, not a feature
 * list: curiosity (the promise) → pain (the old way, FELT through a pinned,
 * scroll-driven chat that piles up until it chokes) → relief (the flip) →
 * proof (the real product, screens not mockups) → aspiration (the worker's
 * side) → urgency (founding, real numbers) → the obvious next step.
 *
 * Motion is scroll-LINKED, not just scroll-triggered: the hero shot flattens
 * out of 3D as you start scrolling, the old-way stage pins for 340vh while
 * messages accumulate, story screens drift on parallax, and the final panel
 * grows to meet you. Everything collapses to a static, complete page under
 * prefers-reduced-motion.
 *
 * Every product visual is a screenshot of the live system
 * (e2e/capture-landing.mjs) — never a mockup.
 */
export function Landing() {
  // Somebody already signed in has no business on the pitch — the app icon
  // and a shared "/" link should land them in the product, not the marketing.
  const role = getRole();
  if (role === 'owner') return <Navigate to="/home" replace />;
  if (role === 'staff') return <Navigate to="/today" replace />;

  useEffect(() => {
    if (!window.location.hash) return;
    const id = window.location.hash.slice(1);
    requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView());
  }, []);

  return (
    <div className="min-h-[100dvh] overflow-x-clip bg-background font-sans text-foreground selection:bg-primary/20">
      <Link
        to="/signup"
        onClick={() => track('cta_click', { location: 'founding_strip', label: 'reserve' })}
        className="group flex min-h-11 items-center justify-center gap-3 bg-foreground px-4 py-2.5 text-center text-xs font-bold text-background sm:text-sm"
      >
        <span>
          <span className="text-primary">
            {t('Founding households are now being selected in the GCC.')}
          </span>{' '}
          {t('Personal setup, direct founder access and launch pricing for the first cohort.')}
        </span>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-background px-3 py-1.5 text-[11px] font-black text-foreground transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5 sm:text-xs">
          {t('Reserve founding access')}
          <Icon icon="ph:arrow-right-bold" className="h-3.5 w-3.5 rtl:-scale-x-100" aria-hidden />
        </span>
      </Link>

      <Nav />

      <main>
        <Hero />
        <AutocompleteDemo />
        <OldWay />
        <Category />
        <StorySequence />
        <WorkerTransformation />
        <HouseholdRemembers />
        <ClosingRitual />
        <Channels />
        <ProductTruth />
        <FounderStory />
        <Pricing />
        <HouseholdVault />
        <FoundingAccess />
        <Questions />
        <FinalCall />
      </main>

      <Footer />
    </div>
  );
}

// ─── The scroll engine ───────────────────────────────────────────────────

type Ref<T> = { current: T | null };

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** One rAF-throttled scroll subscription pattern, shared by every effect. */
function onScrollRaf(update: () => void): () => void {
  let raf = 0;
  const handler = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(update);
  };
  update();
  window.addEventListener('scroll', handler, { passive: true });
  window.addEventListener('resize', handler);
  return () => {
    window.removeEventListener('scroll', handler);
    window.removeEventListener('resize', handler);
    cancelAnimationFrame(raf);
  };
}

/** Window scroll position (stays 0 for reduced-motion users). */
function useScrollY(): number {
  const [y, setY] = useState(0);
  useEffect(() => {
    if (reducedMotion()) return;
    return onScrollRaf(() => setY(window.scrollY));
  }, []);
  return y;
}

/**
 * Scroll progress (0→1) through a tall pinned section: 0 when its top
 * reaches the viewport top, 1 when its bottom leaves. Drives every pinned,
 * scroll-linked scene. Reduced-motion users get the final state (1).
 */
function useScrollProgress(ref: Ref<HTMLElement>): number {
  const [p, setP] = useState(0);
  useEffect(() => {
    if (reducedMotion()) {
      setP(1);
      return;
    }
    return onScrollRaf(() => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      setP(total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 1);
    });
  }, [ref]);
  return p;
}

/**
 * How far an element has travelled into the viewport: 0 when its top enters
 * at the bottom edge, 1 once it covers the first 55% of the screen. Powers
 * the final panel that grows to meet the visitor.
 */
function useEntranceProgress(ref: Ref<HTMLElement>): number {
  const [p, setP] = useState(0);
  useEffect(() => {
    if (reducedMotion()) {
      setP(1);
      return;
    }
    return onScrollRaf(() => {
      const el = ref.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setP(Math.min(1, Math.max(0, (window.innerHeight - top) / (window.innerHeight * 0.55))));
    });
  }, [ref]);
  return p;
}

/** Gentle parallax: children drift against the scroll as they cross the
 *  viewport center (±34px). Reduced-motion keeps them still. */
function Parallax({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    if (reducedMotion()) return;
    return onScrollRaf(() => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const center = r.top + r.height / 2 - window.innerHeight / 2;
      setOffset(Math.max(-1, Math.min(1, center / window.innerHeight)) * -34);
    });
  }, []);
  return (
    <div
      ref={ref}
      style={{ transform: `translateY(${offset}px)` }}
      className="will-change-transform"
    >
      {children}
    </div>
  );
}

/** Scroll-triggered entrance: rises and settles once, never again. */
function Reveal({
  children,
  delay = 0,
  className = '',
}: { children: ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (reducedMotion()) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`${className} transition-all duration-700 ease-out will-change-transform ${
        shown ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      }`}
    >
      {children}
    </div>
  );
}

// ─── Chrome ──────────────────────────────────────────────────────────────

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <nav
      className={`sticky top-0 z-50 border-b bg-background/85 backdrop-blur-xl transition-shadow duration-300 ${
        scrolled ? 'border-border shadow-elev-2' : 'border-transparent'
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <MarketingMark />
        <div className="hidden items-center gap-8 text-sm font-semibold text-muted-foreground lg:flex">
          <a href="#how" className="transition-colors hover:text-foreground">
            {t('How it works')}
          </a>
          <a href="#old-way" className="transition-colors hover:text-foreground">
            {t('The old way')}
          </a>
          <a href="#memory" className="transition-colors hover:text-foreground">
            {t('Household memory')}
          </a>
          <a href="#pricing" className="transition-colors hover:text-foreground">
            {t('Plans')}
          </a>
          <a href="#questions" className="transition-colors hover:text-foreground">
            {t('Questions')}
          </a>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/login"
            className="hidden text-sm font-semibold text-muted-foreground hover:text-foreground sm:block"
          >
            {t('Sign in')}
          </Link>
          <Link
            to="/signup"
            onClick={() => track('cta_click', { location: 'nav', label: 'reserve' })}
            className="inline-flex min-h-11 items-center rounded-full bg-primary px-5 text-sm font-bold text-primary-foreground shadow-elev-2 transition hover:-translate-y-0.5 active:scale-[0.98]"
          >
            {t('Reserve founding access')}
          </Link>
        </div>
      </div>
    </nav>
  );
}

function MarketingMark() {
  return (
    <Link to="/" className="block shrink-0" aria-label="Famanage home" dir="ltr">
      <img src={logoFull} alt="Famanage" className="h-8 w-auto sm:h-9" draggable={false} />
    </Link>
  );
}

// ─── Act I · Curiosity — the promise, then the product in 3D ─────────────

function Hero() {
  return (
    <header className="px-4 pb-10 pt-14 text-center sm:px-6 sm:pt-20 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <p className="fm-hero-rise text-[11px] font-black uppercase tracking-[0.2em] text-primary">
          {t('Operational autocomplete for staffed homes')}
        </p>
        <h1
          className="fm-hero-rise mx-auto mt-5 max-w-[14ch] font-heading text-[clamp(2.75rem,7vw,6rem)] font-bold leading-[0.95] tracking-[-0.05em]"
          style={{ animationDelay: '120ms' }}
        >
          {t('Say it once. Your household carries it through.')}
        </h1>
        <p
          className="fm-hero-rise mx-auto mt-7 max-w-[42rem] text-lg leading-8 text-muted-foreground sm:text-xl"
          style={{ animationDelay: '240ms' }}
        >
          {t(
            'You give a short, natural request. Famanage expands it with your household’s memory, hands each worker the right next step in their own language — and keeps the follow-through alive until it is actually done.',
          )}
        </p>
        <div
          className="fm-hero-rise mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
          style={{ animationDelay: '360ms' }}
        >
          <Link
            to="/signup"
            onClick={() => track('cta_click', { location: 'hero', label: 'reserve' })}
            className="inline-flex min-h-14 w-full items-center justify-center rounded-full bg-primary px-8 text-base font-bold text-primary-foreground shadow-elev-2 transition hover:-translate-y-0.5 hover:shadow-elev-3 active:scale-[0.98] sm:w-auto"
          >
            {t('Reserve founding access')}
            <Icon
              icon="ph:arrow-right-bold"
              className="ms-2 h-4 w-4 rtl:-scale-x-100"
              aria-hidden
            />
          </Link>
          {/* biome-ignore lint/a11y/useValidAnchor: in-page navigation anchor; onClick only records an analytics event */}
          <a
            href="#how"
            onClick={() => track('cta_click', { location: 'hero', label: 'see_how' })}
            className="inline-flex min-h-14 w-full items-center justify-center rounded-full border border-border bg-card px-8 text-base font-bold transition hover:bg-secondary sm:w-auto"
          >
            {t('See how one sentence becomes handled')}
            <Icon icon="ph:arrow-down-bold" className="ms-2 h-4 w-4" aria-hidden />
          </a>
        </div>
        <p
          className="fm-hero-rise mt-4 text-sm font-medium text-muted-foreground"
          style={{ animationDelay: '440ms' }}
        >
          {t('First 50 founding households · GCC · no card until launch eligibility is confirmed')}
        </p>
      </div>
      <HeroShot />
    </header>
  );
}

/** The product floats up in 3D and flattens into the page as the visitor
 *  starts scrolling — the Apple-style "the object lands" moment. */
function HeroShot() {
  const y = useScrollY();
  const settle = Math.min(1, y / 420);
  const tilt = 16 * (1 - settle);
  const scale = 0.95 + 0.05 * settle;
  return (
    <Reveal className="mx-auto mt-14 max-w-[1200px] [perspective:1600px] sm:mt-20">
      <div
        style={{
          transform: `rotateX(${tilt}deg) scale(${scale})`,
          transformOrigin: 'center 10%',
        }}
        className="will-change-transform"
      >
        <BrowserShot
          src={ownerLiveHome}
          alt={t("Famanage Live Home — the owner's morning screen")}
        />
      </div>
      <figcaption className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">
        {t('Real product. Real house. Running today.')}
      </figcaption>
    </Reveal>
  );
}

// ─── The signature moment — operational autocomplete, demonstrated ───────

type DemoStage = {
  label: string;
  icon: string;
  body: string;
  worker?: boolean;
  closing?: boolean;
};

type DemoRequest = {
  id: string;
  sentence: string;
  stages: DemoStage[];
};

const DEMO_REQUESTS: DemoRequest[] = [
  {
    id: 'pool-filter',
    sentence: 'Buy a new pool filter.',
    stages: [
      {
        label: 'What Famanage understood',
        icon: 'ph:crosshair-bold',
        body: 'A compatible filter — bought, installed and verified, with the next replacement already on record. Not a task. An outcome.',
      },
      {
        label: 'What your household remembered',
        icon: 'ph:brain-bold',
        body: 'The filter model is on file, replaced 14 months ago. So is the supplier you trust.',
      },
      {
        label: 'The one question it asks',
        icon: 'ph:question-bold',
        body: '“Same model as last time?” — the only thing it will not safely assume.',
      },
      {
        label: 'What your worker receives',
        icon: 'ph:user-bold',
        worker: true,
        body: 'One action, in their own language: “Order the filter on file from the usual supplier — and keep the receipt.” Not the whole project. Just the next right move.',
      },
      {
        label: 'What it keeps alive',
        icon: 'ph:hourglass-bold',
        body: 'Delivery, installation and the fit check are tracked as waits — not reminders you have to send. A replacement obligation is created for next year.',
      },
      {
        label: 'How it closes',
        icon: 'ph:seal-check-bold',
        closing: true,
        body: 'Installed. Receipt filed. Fit verified. Next replacement remembered. You wrote five words.',
      },
    ],
  },
  {
    id: 'gaia',
    sentence: 'Book Gaia for Friday.',
    stages: [
      {
        label: 'What Famanage understood',
        icon: 'ph:crosshair-bold',
        body: 'A confirmed Friday dinner at Gaia, to your household’s standard — seating, allergies, timing and transport handled.',
      },
      {
        label: 'What your household remembered',
        icon: 'ph:brain-bold',
        body: 'The window table you prefer. The shellfish allergy on file. That you like to sit down by 20:00.',
      },
      {
        label: 'The one question it asks',
        icon: 'ph:question-bold',
        body: '“Table for two, or is family joining?” — guest count is the one thing it will not guess.',
      },
      {
        label: 'What your worker receives',
        icon: 'ph:user-bold',
        worker: true,
        body: '“Review the menu against the allergy on file, book the window table for Friday at 20:00, then confirm here.”',
      },
      {
        label: 'What it keeps alive',
        icon: 'ph:hourglass-bold',
        body: 'It waits on the restaurant’s confirmation — and arranges the driver for 19:30 the moment the booking lands.',
      },
      {
        label: 'How it closes',
        icon: 'ph:seal-check-bold',
        closing: true,
        body: 'Confirmed: Friday 20:00, window table, allergy noted with the kitchen, driver booked. You wrote four words.',
      },
    ],
  },
  {
    id: 'smiths',
    sentence: 'The Smiths are coming tomorrow.',
    stages: [
      {
        label: 'What Famanage understood',
        icon: 'ph:crosshair-bold',
        body: 'The household guest-ready by tomorrow — rooms, food, timing and arrival, coordinated around one short sentence.',
      },
      {
        label: 'What your household remembered',
        icon: 'ph:brain-bold',
        body: 'The Smiths stayed in March: the guest-room setup is on file, no nuts in the house, kids swim after lunch.',
      },
      {
        label: 'The one question it asks',
        icon: 'ph:question-bold',
        body: '“Are they staying for dinner?” — scope is the one thing it will not assume.',
      },
      {
        label: 'What your workers receive',
        icon: 'ph:user-bold',
        worker: true,
        body: 'Each person gets only their piece, one action at a time: “Prepare the guest room and check the pool towels.”',
      },
      {
        label: 'What it keeps alive',
        icon: 'ph:hourglass-bold',
        body: 'Arrival time is pending — the grocery run waits on that answer instead of blocking everything else.',
      },
      {
        label: 'How it closes',
        icon: 'ph:seal-check-bold',
        closing: true,
        body: 'Rooms ready. Food planned. Arrival coordinated. Nobody was project-managed. You wrote five words.',
      },
    ],
  },
];

/**
 * The central demonstration: a short owner sentence expands into understood
 * intent, remembered context, one honest question, the worker's next action,
 * the waits and obligations preserved, and a closed outcome. Faithful to the
 * example chains in docs/execution/architecture/02-operational-chain-domain.md
 * — and labeled as what it is: a preview of capabilities in founding rollout,
 * not a claim about what is shipped today.
 */
const DEFAULT_DEMO: DemoRequest = (() => {
  const first = DEMO_REQUESTS[0];
  if (!first) throw new Error('DEMO_REQUESTS must not be empty');
  return first;
})();

function AutocompleteDemo() {
  const [selected, setSelected] = useState<DemoRequest>(DEFAULT_DEMO);
  return (
    <section id="how" className="px-4 pb-24 sm:px-6 sm:pb-32 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <Reveal className="text-center">
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
            {t('How it works')}
          </p>
          <h2 className="mx-auto mt-5 max-w-[16ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            {t('One sentence in. The whole operation out.')}
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-muted-foreground">
            {t(
              'Neither you nor your staff should have to hold the entire chain in your heads. Pick a request — watch what Famanage does with it.',
            )}
          </p>
        </Reveal>

        <Reveal delay={140}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {DEMO_REQUESTS.map((request) => (
              <button
                key={request.id}
                type="button"
                aria-pressed={selected.id === request.id}
                onClick={() => {
                  setSelected(request);
                  track('demo_select', { request: request.id });
                }}
                className={`min-h-12 rounded-full border px-5 text-sm font-bold transition sm:text-base ${
                  selected.id === request.id
                    ? 'border-primary bg-primary text-primary-foreground shadow-elev-2'
                    : 'border-border bg-card hover:bg-secondary'
                }`}
              >
                “{t(request.sentence)}”
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={220}>
          <div className="mx-auto mt-10 max-w-3xl overflow-hidden rounded-card border border-border bg-card shadow-elev-2">
            <div className="flex items-center gap-3 border-b border-border bg-secondary px-5 py-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-card shadow-elev-1">
                <Icon
                  icon="ph:paper-plane-tilt-fill"
                  className="h-4 w-4 text-primary"
                  aria-hidden
                />
              </span>
              <p className="text-sm font-bold sm:text-base" dir="auto">
                {t('You said:')} “{t(selected.sentence)}”
              </p>
            </div>
            <ol key={selected.id} aria-live="polite" className="divide-y divide-border">
              {selected.stages.map((stage, i) => (
                <li
                  key={stage.label}
                  className="fm-step-rise flex gap-4 px-5 py-5 sm:px-7"
                  style={{ animationDelay: `${i * 550}ms` }}
                >
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-row ${
                      stage.closing
                        ? 'bg-wealth/10 text-wealth'
                        : stage.worker
                          ? 'bg-magic/10 text-magic'
                          : 'bg-primary/10 text-primary'
                    }`}
                  >
                    <Icon icon={stage.icon} className="h-5 w-5" aria-hidden />
                  </span>
                  <div>
                    <p
                      className={`text-[11px] font-black uppercase tracking-[0.18em] ${
                        stage.closing
                          ? 'text-wealth'
                          : stage.worker
                            ? 'text-magic'
                            : 'text-muted-foreground'
                      }`}
                    >
                      {t(stage.label)}
                    </p>
                    <p className="mt-1.5 max-w-[34rem] text-[15px] font-medium leading-7">
                      {t(stage.body)}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <p className="mx-auto mt-5 max-w-2xl text-center text-xs font-medium leading-6 text-muted-foreground/80">
            {t(
              'A conceptual preview on the real household model. Intent expansion and durable follow-through are in founding rollout — structured work, receipts, household memory and multilingual delivery run today.',
            )}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Act II · Pain — the old way, FELT ───────────────────────────────────

const CHAT: Array<{ side: 'them' | 'you'; text: string; time: string; voice?: boolean }> = [
  { side: 'you', text: 'did you buy the pool filter??', time: '09:14' },
  { side: 'you', text: 'Send the receipt photo again. I lost it.', time: '09:15' },
  { side: 'them', text: 'Voice message · 0:47', time: '09:31', voice: true },
  { side: 'them', text: 'The AC guy came but nobody told me', time: '12:03' },
  { side: 'you', text: 'did you do it?', time: '15:22' },
  { side: 'them', text: 'what time tomorrow?', time: '21:38' },
  { side: 'you', text: 'sorry — just seeing this. was in meetings all day', time: '23:47' },
  { side: 'you', text: 'Maria knew all of this. She left in May.', time: '23:52' },
];

/**
 * The signature scene. The stage pins for 340vh; the scroll wheel itself
 * piles message after message onto the phone until the screen is crowded,
 * then the whole chat blurs and sinks while the verdict rises over it.
 * The visitor does not read about the chaos — they produce it, scroll by
 * scroll, and feel it suffocate.
 */
function OldWay() {
  const stageRef = useRef<HTMLDivElement>(null);
  const p = useScrollProgress(stageRef);
  const punchIn = Math.min(1, Math.max(0, (p - 0.78) / 0.16));
  return (
    <section id="old-way" className="border-y border-border bg-secondary">
      <div className="mx-auto max-w-7xl px-4 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8">
        <Reveal>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-urgent">
            {t('The old way')}
          </p>
          <h2 className="mx-auto mt-5 max-w-[16ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            {t('This is how your home runs today.')}
          </h2>
        </Reveal>
      </div>

      <div ref={stageRef} className="relative" style={{ height: '340vh' }}>
        <div className="sticky top-0 flex h-screen items-center justify-center overflow-hidden px-4">
          {/* The phone, drowning by scroll */}
          <div
            className="w-full max-w-md will-change-transform"
            style={{
              opacity: 1 - punchIn * 0.85,
              filter: `blur(${punchIn * 7}px)`,
              transform: `scale(${1 - punchIn * 0.07}) translateY(${punchIn * 30}px)`,
            }}
          >
            <div className="overflow-hidden rounded-[2rem] border border-border bg-card shadow-elev-3">
              <div className="flex items-center gap-3 border-b border-border bg-card px-5 py-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary">
                  <Icon
                    icon="ph:users-three-bold"
                    className="h-[18px] w-[18px] text-muted-foreground"
                    aria-hidden
                  />
                </span>
                <div>
                  <p className="text-sm font-bold">{t('House group')}</p>
                  <p className="text-[11px] text-muted-foreground">{t('6 · online')}</p>
                </div>
              </div>
              <div className="flex h-[52vh] min-h-[340px] flex-col justify-end gap-2.5 bg-secondary/60 p-4">
                {CHAT.map((m, i) => {
                  const vis = Math.min(1, Math.max(0, (p - (0.04 + i * 0.09)) / 0.05));
                  return (
                    <div
                      key={`${m.time}-${i}`}
                      className={`flex ${m.side === 'you' ? 'justify-end' : 'justify-start'}`}
                      style={{
                        opacity: vis,
                        transform: `translateY(${(1 - vis) * 14}px)`,
                      }}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-2.5 shadow-elev-1 ${
                          m.side === 'you'
                            ? 'rounded-ee-md bg-primary/10 text-foreground'
                            : 'rounded-es-md bg-card'
                        }`}
                      >
                        <p
                          className="flex items-center gap-2 text-sm font-medium leading-snug"
                          dir="auto"
                        >
                          {m.voice && (
                            <Icon
                              icon="ph:play-circle-fill"
                              className="h-6 w-6 shrink-0 text-primary"
                              aria-hidden
                            />
                          )}
                          {t(m.text)}
                        </p>
                        <p className="mt-1 text-end text-[10px] text-muted-foreground">{m.time}</p>
                      </div>
                    </div>
                  );
                })}
                <div
                  className="flex justify-start"
                  style={{ opacity: Math.min(1, Math.max(0, (p - 0.74) / 0.05)) }}
                >
                  <div className="flex items-center gap-1.5 rounded-2xl rounded-es-md bg-card px-4 py-3 shadow-elev-1">
                    <span className="fm-typing-dot" />
                    <span className="fm-typing-dot" style={{ animationDelay: '150ms' }} />
                    <span className="fm-typing-dot" style={{ animationDelay: '300ms' }} />
                    <span className="ms-1.5 text-[11px] text-muted-foreground">{t('typing…')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* The verdict, rising over the drowned chat */}
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center px-6"
            style={{ opacity: punchIn, transform: `translateY(${(1 - punchIn) * 46}px)` }}
          >
            <div className="max-w-3xl text-center">
              <p className="font-heading text-3xl font-bold leading-[1.02] tracking-[-0.04em] sm:text-6xl">
                {t('The old system is you remembering everything.')}
              </p>
              <p className="mx-auto mt-6 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
                {t(
                  'Not WhatsApp. Not your staff. The system — where every preference, every supplier and every follow-up lives in your head, and walks out the door each time a worker leaves.',
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── The new category ────────────────────────────────────────────────────

const CATEGORY_FACETS = [
  {
    icon: 'ph:text-t-bold',
    title: 'It expands short intent',
    body: 'Five words become the full operation they imply — not a longer to-do list for you.',
  },
  {
    icon: 'ph:brain-bold',
    title: 'It remembers your household',
    body: 'Preferences, suppliers, schedules and standards — retrieved exactly when they matter.',
  },
  {
    icon: 'ph:compass-bold',
    title: 'It guides the next decision',
    body: 'Each worker sees the next useful action, never a dumped project plan.',
  },
  {
    icon: 'ph:hourglass-bold',
    title: 'It keeps obligations alive',
    body: 'Deliveries, follow-ups and future replacements stay open until they truly close.',
  },
  {
    icon: 'ph:arrows-clockwise-bold',
    title: 'It learns from what happened',
    body: 'Corrections and outcomes shape how the next operation is handled.',
  },
  {
    icon: 'ph:users-three-bold',
    title: 'It survives staff change',
    body: 'The household’s operating knowledge stays when people move on.',
  },
];

function Category() {
  return (
    <section id="category" className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1380px] overflow-hidden rounded-card bg-foreground px-5 py-20 text-background sm:px-10 sm:py-28">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
              {t('A new category')}
            </p>
            <h2 className="mx-auto mt-5 max-w-[16ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
              {t('Operational autocomplete for staffed homes.')}
            </h2>
            <p className="mx-auto mt-7 max-w-2xl text-lg leading-8 text-background/60">
              {t(
                'The way autocomplete completes a sentence, Famanage completes an intention. Like an experienced household manager standing quietly beside every worker — with your household’s memory in hand.',
              )}
            </p>
          </Reveal>
        </div>
        <div className="mx-auto mt-14 grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CATEGORY_FACETS.map((facet, i) => (
            <Reveal key={facet.title} delay={i * 80}>
              <article className="h-full rounded-card border border-background/15 bg-background/5 p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-row bg-background/10">
                  <Icon icon={facet.icon} className="h-5 w-5 text-primary" aria-hidden />
                </span>
                <h3 className="mt-4 text-base font-bold tracking-tight">{t(facet.title)}</h3>
                <p className="mt-2 text-sm leading-6 text-background/55">{t(facet.body)}</p>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={200}>
          <p className="mx-auto mt-12 max-w-3xl text-center text-sm font-semibold leading-7 text-background/45">
            {t(
              'Not a task manager. Not staff monitoring. Not a training platform, a payroll company or a smart-home remote. The intelligence layer underneath how your home runs.',
            )}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Act IV · Proof — the loop, on real screens ──────────────────────────

const STORY = [
  {
    label: '01 · Attention',
    title: 'Open once. Know what needs you.',
    body: 'Live Home gives you the day in one glance: the decisions waiting on you, today’s work, team status. Nothing else demands your attention.',
    shot: ownerLiveHome,
    alt: 'Live Home — decisions and running work',
  },
  {
    label: '02 · Command',
    title: 'Say it naturally. The system does the structuring.',
    body: 'Type the instruction as you would say it. Famanage identifies the person, action, timing and proof, then turns it into clear work.',
    shot: ownerTasks,
    alt: 'Structured work — every task with an owner and a state',
  },
  {
    label: '03 · Money loop',
    title: 'Photograph the receipt. Close the loop.',
    body: 'A receipt becomes an expense, approved expenses become payroll. Famanage reads the purchase and proposes the next action — you only approve.',
    shot: ownerMoney,
    alt: 'The money loop — house cost, wages, and the next decision',
  },
];

function StorySequence() {
  return (
    <section id="today" className="px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
            {t('Real product, running today')}
          </p>
          <h2 className="mt-5 max-w-[15ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            {t('This is not a concept. It already runs a real house.')}
          </h2>
        </Reveal>
        <div className="mt-14 space-y-20 sm:space-y-28">
          {STORY.map((item, index) => (
            <article
              key={item.label}
              className="grid gap-9 lg:grid-cols-2 lg:items-center lg:gap-20"
            >
              <Reveal className={index === 1 ? 'lg:order-2' : ''}>
                <Parallax>
                  <BrowserShot src={item.shot} alt={t(item.alt)} />
                </Parallax>
              </Reveal>
              <Reveal delay={120} className={index === 1 ? 'lg:order-1' : ''}>
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
                  {t(item.label)}
                </p>
                <h3 className="mt-5 max-w-[12ch] font-heading text-3xl font-bold leading-[1.0] tracking-[-0.035em] sm:text-5xl">
                  {t(item.title)}
                </h3>
                <p className="mt-6 max-w-[36rem] text-lg leading-8 text-muted-foreground">
                  {t(item.body)}
                </p>
              </Reveal>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── The worker transformation ───────────────────────────────────────────

const WORKER_GAINS = [
  'Better memory',
  'Better preparation',
  'Better sequencing',
  'Better anticipation',
  'Better communication',
  'Better follow-through',
];

function WorkerTransformation() {
  return (
    <section
      id="worker"
      className="border-y border-border bg-secondary px-4 py-20 sm:px-6 sm:py-28 lg:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center lg:gap-24">
          <div>
            <Reveal>
              <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
                {t('For the people who run your home')}
              </p>
              <h2 className="mt-5 font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-5xl">
                {t('Good people do extraordinary work when they stop having to guess.')}
              </h2>
              <p className="mt-6 max-w-[36rem] text-lg leading-8 text-muted-foreground">
                {t(
                  'Your staff was never the problem. They were asked to run your home without its memory, its context or its standards — and judged when they guessed wrong.',
                )}
              </p>
            </Reveal>

            <div className="mt-9 space-y-4">
              <Reveal delay={80}>
                <article className="rounded-card border border-border bg-card p-5 shadow-elev-1 sm:p-6">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-muted-foreground">
                    {t('Before')} · {t('“Book a restaurant.”')}
                  </p>
                  <p className="mt-3 text-[15px] leading-7 text-muted-foreground">
                    {t(
                      'Which one? What time? How many people? Any allergies? Who is driving? Five words, five guesses — and a wrong answer comes back to you at 21:00.',
                    )}
                  </p>
                </article>
              </Reveal>
              <Reveal delay={180}>
                <article className="rounded-card border border-primary/25 bg-primary/5 p-5 shadow-elev-1 sm:p-6">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-primary">
                    {t('With Famanage beside them')}
                  </p>
                  <p className="mt-3 text-[15px] font-medium leading-7">
                    {t(
                      'The same worker sees the window table you prefer, the allergy on file and the 20:00 standard — books, confirms the driver for 19:30 and follows up. Like someone who has run this house for years.',
                    )}
                  </p>
                  <p className="mt-3 text-xs font-medium leading-5 text-muted-foreground">
                    {t('Context-aware assistance is in founding rollout.')}
                  </p>
                </article>
              </Reveal>
            </div>

            <Reveal delay={260}>
              <p className="mt-8 font-heading text-xl font-bold tracking-tight">
                {t('The transformation is your worker plus Famanage. Never instead of them.')}
              </p>
              <p className="mt-3 text-sm font-medium leading-6 text-muted-foreground">
                {t(
                  'Free for staff, always. Your employer invites you; you’re set up in two minutes, in your own language.',
                )}
              </p>
            </Reveal>
          </div>
          <Reveal delay={150}>
            <Parallax>
              <PhoneShot src={staffToday} alt={t('The worker app — today, one action at a time')} />
            </Parallax>
          </Reveal>
        </div>

        <div className="mt-14 flex flex-wrap items-center justify-center gap-3">
          {WORKER_GAINS.map((gain, i) => (
            <Reveal key={gain} delay={i * 70}>
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2.5 text-sm font-bold shadow-elev-1">
                <Icon icon="ph:plus-bold" className="h-3.5 w-3.5 text-primary" aria-hidden />
                {t(gain)}
              </span>
            </Reveal>
          ))}
        </div>

        <Reveal delay={140}>
          <p className="mx-auto mt-12 max-w-3xl border-t border-border pt-8 text-center text-sm font-semibold leading-7 text-muted-foreground">
            {t(
              'Famanage is support, not surveillance: no location tracking, no productivity scores, no leaderboards, no hidden monitoring. Workers can say unclear, unsafe, blocked or out of role — and it never counts against them.',
            )}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

// ─── The household remembers ─────────────────────────────────────────────

const REMEMBERED = [
  { icon: 'ph:heart-bold', label: 'Preferences' },
  { icon: 'ph:clock-counter-clockwise-bold', label: 'Routines' },
  { icon: 'ph:storefront-bold', label: 'Suppliers and vendors' },
  { icon: 'ph:baby-bold', label: "Children's schedules" },
  { icon: 'ph:paw-print-bold', label: 'Pet care' },
  { icon: 'ph:wrench-bold', label: 'Maintenance cycles' },
  { icon: 'ph:warning-circle-bold', label: 'Allergies' },
  { icon: 'ph:fork-knife-bold', label: 'Restaurant preferences' },
  { icon: 'ph:receipt-bold', label: 'What purchases set in motion' },
  { icon: 'ph:medal-bold', label: 'Household standards' },
  { icon: 'ph:pencil-line-bold', label: 'Previous corrections' },
  { icon: 'ph:sun-bold', label: 'What a perfect day looks like' },
];

function HouseholdRemembers() {
  return (
    <section id="memory" className="px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center lg:gap-24">
          <div>
            <Reveal>
              <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
                {t('Household memory')}
              </p>
              <h2 className="mt-5 max-w-[14ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
                {t('Workers change. Your household should not forget.')}
              </h2>
              <p className="mt-6 max-w-[36rem] text-lg leading-8 text-muted-foreground">
                {t(
                  'Every well-run home operates on knowledge that was never written down — until the person holding it gives notice, and the house starts from zero. Famanage makes that knowledge belong to the household.',
                )}
              </p>
              <p className="mt-6 max-w-[36rem] font-heading text-xl font-bold leading-8 tracking-tight">
                {t('Software can be copied. Your household’s lived history cannot.')}
              </p>
              <p className="mt-6 max-w-[36rem] text-sm leading-7 text-muted-foreground">
                {t(
                  'The longer Famanage lives in your home, the less you need to say — and the more a new worker already knows on day one. Memory foundations run today; compounding intelligence and faster staff transitions are the long-term direction.',
                )}
              </p>
            </Reveal>
          </div>
          <Reveal delay={150}>
            <Parallax>
              <BrowserShot
                src={ownerMemory}
                alt={t('Household memory — the home’s standards and preferences, kept')}
              />
            </Parallax>
          </Reveal>
        </div>
        <div className="mt-14 flex flex-wrap justify-center gap-3">
          {REMEMBERED.map((item, i) => (
            <Reveal key={item.label} delay={i * 50}>
              <span className="inline-flex items-center gap-2.5 rounded-full border border-border bg-card px-4 py-2.5 text-sm font-bold shadow-elev-1">
                <Icon icon={item.icon} className="h-4 w-4 text-primary" aria-hidden />
                {t(item.label)}
              </span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── The owner's two-minute close ────────────────────────────────────────

function ClosingRitual() {
  return (
    <section id="ritual" className="px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-24">
          <Reveal>
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
              {t('The two-minute close')}
            </p>
            <h2 className="mt-5 max-w-[12ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
              {t('“I finished managing the house for today.”')}
            </h2>
            <p className="mt-6 max-w-[30rem] text-lg leading-8 text-muted-foreground">
              {t(
                'That sentence is the product. Not another dashboard to live in — a short evening ritual, and then your day is yours again.',
              )}
            </p>
          </Reveal>
          <div className="divide-y divide-border border-y border-border">
            <Moment
              time="21:40"
              icon="ph:scales-bold"
              tone="text-magic"
              title={t('What needs your decision')}
              body={t(
                'Two considered items, not forty notifications. You decide; the house executes.',
              )}
              delay={0}
            />
            <Moment
              time="21:41"
              icon="ph:check-circle-fill"
              tone="text-wealth"
              title={t('What ran correctly')}
              body={t(
                'Confirmed at a glance, then ignored. Completed means truly closed — not tapped done.',
              )}
              delay={100}
            />
            <Moment
              time="21:41"
              icon="ph:hourglass-bold"
              tone="text-task"
              title={t('What is waiting externally')}
              body={t(
                'On suppliers, deliveries and other people — each wait has a name and an owner that is not you.',
              )}
              delay={200}
            />
            <Moment
              time="21:42"
              icon="ph:brain-bold"
              tone="text-primary"
              title={t('What the household remembered')}
              body={t('So tomorrow starts smarter than today did.')}
              delay={300}
            />
          </div>
        </div>
        <Reveal delay={200}>
          <p className="mx-auto mt-14 max-w-2xl text-center font-heading text-2xl font-bold leading-snug tracking-tight sm:text-3xl">
            {t('Then you close Famanage — and the house keeps running.')}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Moment({
  time,
  icon,
  tone,
  title,
  body,
  delay,
}: { time: string; icon: string; tone: string; title: string; body: string; delay: number }) {
  return (
    <Reveal delay={delay}>
      <article className="grid gap-4 py-7 sm:grid-cols-[76px_52px_1fr] sm:items-start">
        <p className="pt-3 text-sm font-black text-muted-foreground/60">{time}</p>
        <span className="flex h-12 w-12 items-center justify-center rounded-row border border-border bg-card shadow-elev-1">
          <Icon icon={icon} className={`h-6 w-6 ${tone}`} aria-hidden />
        </span>
        <div>
          <h3 className="text-xl font-bold tracking-[-0.02em]">{title}</h3>
          <p className="mt-2 max-w-[38rem] leading-7 text-muted-foreground">{body}</p>
        </div>
      </article>
    </Reveal>
  );
}

// ─── One intelligence, every natural channel ─────────────────────────────

const CHANNELS = [
  { icon: 'ph:device-mobile-bold', label: 'The native app' },
  { icon: 'ph:whatsapp-logo-bold', label: 'WhatsApp' },
  { icon: 'ph:microphone-bold', label: 'Voice' },
  { icon: 'ph:translate-bold', label: 'Short translated text' },
  { icon: 'ph:image-bold', label: 'Images and checklists' },
  { icon: 'ph:footprints-bold', label: 'One action at a time' },
];

const TRANSLATIONS = [
  ['English', 'Wash the car before 3 PM and send photos.'],
  ['עברית', 'שטוף את הרכב לפני 15:00 ושלח תמונות.'],
  ['اردو', 'گاڑی دوپہر 3 بجے سے پہلے دھو دیں اور تصاویر بھیجیں۔'],
  ['Tagalog', 'Hugasan ang kotse bago mag-3 PM at magpadala ng mga litrato.'],
];

function Channels() {
  return (
    <section id="channels" className="px-4 pb-24 sm:px-6 sm:pb-32 lg:px-8">
      <div className="mx-auto max-w-7xl overflow-hidden rounded-card border border-border bg-card py-16 shadow-elev-1 sm:py-24">
        <Reveal className="px-6 text-center sm:px-10">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-card bg-magic/10 text-magic">
            <Icon icon="solar:translation-bold" className="h-7 w-7" aria-hidden />
          </span>
          <h2 className="mx-auto mt-7 max-w-[15ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            {t('One intelligence. Every worker’s natural channel.')}
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-muted-foreground">
            {t(
              'Famanage reaches each person the way they actually understand. The intelligence underneath never changes — only the delivery does.',
            )}
          </p>
        </Reveal>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-3 px-6 sm:px-10">
          {CHANNELS.map((channel, i) => (
            <Reveal key={channel.label} delay={i * 60}>
              <span className="inline-flex items-center gap-2.5 rounded-full border border-border bg-secondary px-4 py-2.5 text-sm font-bold">
                <Icon icon={channel.icon} className="h-4 w-4 text-magic" aria-hidden />
                {t(channel.label)}
              </span>
            </Reveal>
          ))}
        </div>
        <div className="no-scrollbar mt-14 flex gap-4 overflow-x-auto px-6 sm:flex-wrap sm:justify-center sm:px-10">
          {TRANSLATIONS.map(([language, copy], i) => (
            <Reveal key={language} delay={i * 90}>
              <div className="w-[300px] shrink-0 rounded-card border border-border bg-secondary p-6">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">
                  {language}
                </p>
                <p className="mt-4 text-lg font-bold leading-8" dir="auto">
                  {copy}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={200} className="px-6 sm:px-10">
          <p className="mx-auto mt-10 max-w-2xl text-center text-xs font-medium leading-6 text-muted-foreground/80">
            {t(
              'App, WhatsApp, voice and translation run today. Rendering that adapts to each worker’s moment is in founding rollout.',
            )}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Product truth — what exists, what is arriving, what we won't claim ──

const TRUTH_TIERS = [
  {
    icon: 'ph:check-circle-fill',
    tone: 'text-wealth',
    badge: 'bg-wealth/10 text-wealth',
    title: 'Running today',
    points: [
      'Structured household work with owners and states',
      'Worker delivery in four languages, right-to-left included',
      'Receipts, expenses and reimbursements',
      'Routines and recurring schedules',
      'Proof and owner approvals',
      'Household memory foundations',
      'Owner and worker applications',
    ],
  },
  {
    icon: 'ph:rocket-launch-fill',
    tone: 'text-primary',
    badge: 'bg-primary/10 text-primary',
    title: 'In founding rollout',
    points: [
      'Operational-chain expansion of short requests',
      'Context-aware intent and household retrieval',
      'Worker next-action assistance',
      'Durable follow-through and waiting states',
      'Outcome-aware correction',
    ],
  },
  {
    icon: 'ph:signpost-bold',
    tone: 'text-muted-foreground',
    badge: 'bg-secondary text-muted-foreground',
    title: 'Long-term direction',
    points: [
      'Compounding household intelligence',
      'Faster worker transitions',
      'Carefully controlled, low-risk autonomy',
    ],
  },
];

function ProductTruth() {
  return (
    <section id="truth" className="px-4 pb-24 sm:px-6 sm:pb-32 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
            {t('Product truth')}
          </p>
          <h2 className="mt-5 max-w-[16ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            {t('What exists. What is arriving. What we refuse to overclaim.')}
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-5 lg:grid-cols-3">
          {TRUTH_TIERS.map((tier, i) => (
            <Reveal key={tier.title} delay={i * 110}>
              <article className="h-full rounded-card border border-border bg-card p-7 shadow-elev-1 sm:p-9">
                <span
                  className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] font-black uppercase tracking-[0.16em] ${tier.badge}`}
                >
                  <Icon icon={tier.icon} className="h-4 w-4" aria-hidden />
                  {t(tier.title)}
                </span>
                <ul className="mt-7 divide-y divide-border">
                  {tier.points.map((point) => (
                    <li
                      key={point}
                      className="flex items-start gap-3 py-3 text-sm font-semibold leading-6"
                    >
                      <Icon
                        icon="ph:check-bold"
                        className={`mt-0.5 h-4 w-4 shrink-0 ${tier.tone}`}
                        aria-hidden
                      />
                      {t(point)}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={220}>
          <p className="mx-auto mt-12 max-w-3xl text-center text-sm font-semibold leading-7 text-muted-foreground">
            {t(
              'No claim on this page is ahead of this table. Inside the product, AI proposes — people and fixed rules decide, especially for money, allergies, child safety and anything irreversible.',
            )}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

// ─── The Household Vault, and where capacity fits ────────────────────────

/**
 * What the Vault is, before anyone is asked to pay for more of it.
 *
 * Deliberately without a single figure. Every price and every included-storage
 * number in this product comes from `docs/product/pricing.md` through
 * `packages/schemas/src/pricing.ts`, and `apps/web` is not permitted to depend
 * on that package (see apps/web/package.json — it links only `@famanage/i18n`
 * and `@famanage/config`). Typing "$3.99" or "50 GB" into this file to get
 * around that would create a second pricing truth with nothing holding it to
 * the first, which is exactly the failure the pricing document forbids in its
 * own opening line. The sizes and prices are shown on the owner's Storage
 * screen, which reads them from the server, which derives them from that one
 * source. A request to let this page render the same table is filed in
 * docs/workstreams/WS-5-VAULT-CLIENTS/SHARED-CHANGES.json.
 *
 * What is here instead is the part a customer actually needs before buying:
 * what the space holds, what does not count against it, that a Storage Box is
 * capacity and nothing else, and what happens when it fills.
 */
function HouseholdVault() {
  const counts = [
    'Documents — contracts, visas, warranties, manuals',
    'Task photos and the media attached to work',
    'Proof photos, so a finished job has a record',
    'Receipts and receipt PDFs',
    'Voice notes',
    'Every earlier version you choose to keep',
  ];
  const doesNotCount = [
    'Language packs — included, never billed',
    'Offline copies on a phone',
    'Previews and thumbnails the app makes for itself',
    'Searchable text pulled out of your documents',
  ];

  return (
    <section id="vault" className="border-t border-border px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
            {t('The household vault')}
          </p>
          <h2 className="mt-5 max-w-[18ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            {t('Everything the house keeps, in one place.')}
          </h2>
          <p className="mt-7 max-w-2xl text-lg leading-8 text-muted-foreground">
            {t(
              'The visa, the warranty, the photo of the finished job, the receipt from the pharmacy, the voice note nobody had time to type. Filed against the task, the person and the month it belongs to — so it is found later without anybody remembering where it was put.',
            )}
          </p>
        </Reveal>

        <div className="mt-14 grid gap-5 lg:grid-cols-2">
          <Reveal>
            <article className="h-full rounded-card border border-border bg-card p-7 shadow-elev-1 sm:p-9">
              <h3 className="font-heading text-2xl font-bold tracking-[-0.03em]">
                {t('What uses your space')}
              </h3>
              <ul className="mt-6 divide-y divide-border">
                {counts.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 py-3 text-sm font-semibold leading-6"
                  >
                    <Icon
                      icon="ph:check-bold"
                      className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                      aria-hidden
                    />
                    {t(item)}
                  </li>
                ))}
              </ul>
            </article>
          </Reveal>

          <Reveal delay={110}>
            <article className="h-full rounded-card border border-border bg-secondary p-7 sm:p-9">
              <h3 className="font-heading text-2xl font-bold tracking-[-0.03em]">
                {t('What does not')}
              </h3>
              <ul className="mt-6 divide-y divide-border">
                {doesNotCount.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 py-3 text-sm font-semibold leading-6"
                  >
                    <Icon
                      icon="ph:check-bold"
                      className="mt-0.5 h-4 w-4 shrink-0 text-wealth"
                      aria-hidden
                    />
                    {t(item)}
                  </li>
                ))}
              </ul>
            </article>
          </Reveal>
        </div>

        <Reveal delay={220}>
          <div className="mt-5 grid gap-5 lg:grid-cols-3">
            <article className="rounded-card border border-border bg-card p-7 shadow-elev-1">
              <h3 className="text-base font-bold">{t('Storage comes with your plan')}</h3>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">
                {t(
                  'Every paid plan includes cloud storage for the household. The amount is shown with the plan when you choose it, and on your Storage screen after that.',
                )}
              </p>
            </article>
            <article className="rounded-card border border-border bg-card p-7 shadow-elev-1">
              <h3 className="text-base font-bold">{t('A Storage Box adds room. Nothing else.')}</h3>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">
                {t(
                  'It does not add AI actions, translations, receipt scanning, WhatsApp or people on your team, and it needs an active paid plan. Pay monthly, or twelve months up front — twelve months paid up front, not a discount.',
                )}
              </p>
            </article>
            <article className="rounded-card border border-border bg-card p-7 shadow-elev-1">
              <h3 className="text-base font-bold">{t('Full never means lost')}</h3>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">
                {t(
                  'If you run out of room, new uploads pause. Nothing is ever deleted to make space, and viewing, downloading, exporting, cleaning up and billing all keep working. Deleted files sit in Trash — and keep using space — until you empty it.',
                )}
              </p>
            </article>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── The founder story ───────────────────────────────────────────────────

function FounderStory() {
  return (
    <section id="founder" className="border-t border-border px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
      <div className="mx-auto max-w-3xl">
        <Reveal>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
            {t('Why Famanage exists')}
          </p>
          <h2 className="mt-5 font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            {t('Built for one house first.')}
          </h2>
        </Reveal>
        <Reveal delay={120}>
          <p className="mt-8 text-lg leading-8 text-muted-foreground">
            {t(
              'Famanage began as a private system inside our founder’s own home. Asking for things again and again felt demanding; not asking meant they quietly didn’t happen. WhatsApp and broken English became the household’s operating system, purchases were disconnected from what needed to happen next — and the owner remained the household’s memory.',
            )}
          </p>
          <p className="mt-6 text-lg leading-8 text-muted-foreground">
            {t(
              'The workers were never the problem. Most wanted to succeed. They were being asked to run a complex home without its context, its memory or its tools.',
            )}
          </p>
        </Reveal>
        <Reveal delay={200}>
          <blockquote className="my-12 border-s-4 border-primary ps-6 sm:ps-8">
            <p className="font-heading text-2xl font-bold leading-snug tracking-tight sm:text-4xl">
              {t(
                'Workers were not failing because they did not care. They were being asked to operate without the tools required to succeed.',
              )}
            </p>
          </blockquote>
        </Reveal>
        <Reveal delay={260}>
          <p className="text-lg leading-8 text-muted-foreground">
            {t(
              'So the system was built to stand beside the worker, not above them. The founder’s benchmark is not a better task list — it is hearing, six months in: “You will not believe it. Mirza has become like Superman.”',
            )}
          </p>
          <p className="mt-8 text-sm font-semibold leading-7 text-muted-foreground">
            {t(
              'The founder’s own household has run on Famanage for months — every task, every receipt, every payslip.',
            )}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

// ─── What it costs ───────────────────────────────────────────────────────

/**
 * The plans, rendered from `@famanage/schemas` rather than typed here.
 *
 * docs/product/pricing.md is the single source of truth and says so in its
 * first line: if a number on a customer surface disagrees with that file, the
 * file is right and the surface is a bug. `packages/schemas/src/pricing.ts`
 * mirrors it under a test that parses the document's LOCKED tables and fails,
 * naming the cell, when the two drift. Importing from there is what stops this
 * page becoming a fourth place a price can be wrong.
 *
 * The split below is the honest one. Everything in `ALWAYS_INCLUDED` costs us
 * nothing per use — it is the product running on machines we already pay for —
 * so it is included in every paid plan without a number beside it. Everything
 * in the allowance table is a per-use vendor bill (a model call, an OCR page, a
 * transcribed minute, a WhatsApp utility message), which is exactly why those
 * carry a monthly figure and Free carries none of them at all.
 */
const ALWAYS_INCLUDED: readonly string[] = [
  'Unlimited tasks, routines and recurring work',
  'Unlimited staff accounts up to your plan’s worker seats',
  'Owner approvals and proof-of-work sign-off',
  'Payroll, payslips and end-of-service handover',
  'Expenses, budgets and recurring household bills',
  'Household memory — every appliance, supplier and decision, kept',
  'Shift planning, task chains and prepared task packs',
  'Full audit trail and household timeline',
  'Works offline, syncs when the house is back online',
  'Owner and staff apps, iOS, Android and web',
];

/**
 * The Free plan's exclusions as a whole sentence each.
 *
 * Keyed by the exact string `FREE_PLAN.excludes` carries, so a change to the
 * locked list surfaces as an untranslated English line rather than as a silent
 * omission. Written out rather than composed from "No " + a noun, because that
 * concatenation only reads correctly in English.
 */
const FREE_EXCLUSION_COPY: Readonly<Record<string, string>> = {
  'AI actions': 'No AI actions',
  'automatic translation': 'No automatic translation',
  'receipt scanning': 'No receipt scanning',
  'image uploads': 'No image uploads, so no photo proof',
  'voice processing': 'No voice messages',
  'WhatsApp task management': 'No WhatsApp',
};

/** The metered lines, in the order the pricing document lists them. */
const ALLOWANCE_ROWS: ReadonlyArray<{
  key: keyof PlanAllowances;
  label: string;
  unit?: string;
}> = [
  { key: 'aiActions', label: 'AI actions' },
  { key: 'translations', label: 'Automatic translations' },
  { key: 'receiptScans', label: 'Receipt scans' },
  { key: 'voiceMinutes', label: 'Voice minutes' },
  { key: 'whatsappRescueEvents', label: 'WhatsApp rescues' },
];

function Pricing() {
  const priced = PLANS.filter((p) => p.key !== 'private_plus');
  const privatePlus = PLANS.find((p) => p.key === 'private_plus')!;

  return (
    <section id="pricing" className="border-t border-border px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
            {t('Plans')}
          </p>
          <h2 className="mt-5 max-w-[16ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            {t('Priced by the size of your household.')}
          </h2>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-muted-foreground">
            {t(
              'Every plan carries the whole product. What changes is how many people work in your home — and how much of the assistance that costs real money each month you expect to use.',
            )}
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-4">
          {priced.map((plan, i) => {
            const allowances = ALLOWANCES[plan.key];
            const featured = plan.key === 'household';
            return (
              <Reveal key={plan.key} delay={i * 70}>
                <div
                  className={`flex h-full flex-col rounded-card border p-7 ${
                    featured
                      ? 'border-primary bg-card shadow-elev-3'
                      : 'border-border bg-card shadow-elev-1'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-heading text-xl font-bold tracking-tight">{plan.name}</p>
                    {featured ? (
                      <span className="rounded-full bg-primary px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-primary-foreground">
                        {t('Most households')}
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-5 font-heading text-5xl font-bold tracking-[-0.05em]">
                    {plan.from ? (
                      <>
                        <span className="text-2xl font-bold text-muted-foreground">
                          {t('from')}
                        </span>{' '}
                        ${plan.priceUsd}
                      </>
                    ) : (
                      `$${plan.priceUsd}`
                    )}
                  </p>
                  <p className="mt-1.5 text-sm font-semibold text-muted-foreground">
                    {plan.priceUsd === 0 ? t('forever') : t('per month, USD')}
                  </p>
                  <p className="mt-5 min-h-[3rem] text-[15px] font-semibold leading-6">
                    {t(plan.profile)}
                  </p>

                  <dl className="mt-6 grid grid-cols-2 gap-3 border-y border-border py-5 text-sm">
                    <div>
                      <dt className="font-semibold text-muted-foreground">{t('Workers')}</dt>
                      <dd className="mt-0.5 text-lg font-bold">
                        {plan.workers}
                        {plan.from ? (
                          <span className="text-sm font-semibold"> {t('at base')}</span>
                        ) : null}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-muted-foreground">{t('Storage')}</dt>
                      {/* A quantity and its unit are one LTR run in every
                          language. Left to inherit, Hebrew renders "GB 0". */}
                      <dd className="mt-0.5 text-lg font-bold" dir="ltr">
                        {plan.storageGb} GB
                      </dd>
                    </div>
                  </dl>

                  {plan.key === 'free' ? (
                    // Free is written out from FREE_PLAN rather than summarized.
                    // It is the single easiest thing on a pricing page to get
                    // generous about by accident, and generosity here is an
                    // unbounded bill rather than a wording slip.
                    <div className="mt-5 space-y-3 text-sm">
                      <p className="font-semibold">
                        {t('Manual task management, in English, forever free.')}
                      </p>
                      <ul className="space-y-2 text-muted-foreground">
                        {FREE_PLAN.excludes.map((item) => (
                          <li key={item} className="flex items-start gap-2.5">
                            <Icon
                              icon="ph:minus-bold"
                              className="mt-1 h-3.5 w-3.5 shrink-0"
                              aria-hidden
                            />
                            <span>{t(FREE_EXCLUSION_COPY[item] ?? item)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <ul className="mt-5 space-y-2.5 text-sm">
                      {ALLOWANCE_ROWS.map((row) => (
                        <li key={row.key} className="flex items-baseline justify-between gap-3">
                          <span className="text-muted-foreground">{t(row.label)}</span>
                          <span className="font-bold tabular-nums">
                            {allowances![row.key].toLocaleString('en-US')}
                          </span>
                        </li>
                      ))}
                      <li className="pt-1 text-[13px] font-semibold leading-5 text-muted-foreground">
                        {t('Per month. Everything in the list below is included and unmetered.')}
                      </li>
                    </ul>
                  )}

                  <div className="mt-auto pt-7">
                    <Link
                      to="/signup"
                      onClick={() => track('cta_click', { location: 'pricing', label: plan.key })}
                      className={`inline-flex min-h-12 w-full items-center justify-center rounded-full px-6 py-3 text-sm font-bold transition hover:-translate-y-0.5 ${
                        featured
                          ? 'bg-primary text-primary-foreground shadow-elev-2 hover:shadow-elev-3'
                          : 'border border-border bg-background text-foreground hover:border-foreground'
                      }`}
                    >
                      {plan.priceUsd === 0 ? t('Start free') : t('Start with this plan')}
                    </Link>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={120}>
          <div className="mt-6 flex flex-col gap-5 rounded-card border border-border bg-secondary p-7 sm:flex-row sm:items-center sm:justify-between sm:p-9">
            <div>
              <p className="font-heading text-xl font-bold tracking-tight">{privatePlus.name}</p>
              <p className="mt-2 max-w-xl text-[15px] leading-7 text-muted-foreground">
                {t(
                  'Complex or multi-property households, seven workers and up. Priced by contract above the Private Household base.',
                )}
              </p>
            </div>
            <a
              href="mailto:hello@famanage.com?subject=Private%20Plus"
              onClick={() => track('cta_click', { location: 'pricing', label: 'private_plus' })}
              className="inline-flex min-h-12 shrink-0 items-center justify-center rounded-full border border-foreground px-7 text-sm font-bold transition hover:-translate-y-0.5"
            >
              {t('Request a quote')}
            </a>
          </div>
        </Reveal>

        <Reveal delay={160}>
          <div className="mt-14 rounded-card border border-border bg-card p-7 shadow-elev-1 sm:p-10">
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-wealth">
              {t('Included in every paid plan')}
            </p>
            <p className="mt-4 max-w-2xl text-[15px] leading-7 text-muted-foreground">
              {t(
                'None of this is metered, because none of it costs us anything per use. You will never be asked to ration the parts of the product that simply run.',
              )}
            </p>
            <ul className="mt-8 grid gap-x-10 gap-y-3.5 sm:grid-cols-2">
              {ALWAYS_INCLUDED.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-[15px] font-semibold leading-6"
                >
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-wealth/10 text-wealth">
                    <Icon icon="ph:check-bold" className="h-3.5 w-3.5" aria-hidden />
                  </span>
                  {t(item)}
                </li>
              ))}
            </ul>
            <p className="mt-9 max-w-3xl text-sm leading-6 text-muted-foreground">
              {t(
                'A worker’s price never changes because of their nationality or the language they speak. Two workers cost the same whether they share a language or not.',
              )}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── The founding household offer ────────────────────────────────────────

function FoundingAccess() {
  return (
    <section id="founding" className="px-4 pb-24 sm:px-6 sm:pb-32 lg:px-8">
      <div className="mx-auto grid max-w-7xl overflow-hidden rounded-card border border-border bg-card shadow-elev-2 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="p-7 sm:p-12 lg:p-16">
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-urgent">
            {t('Founding access')}
          </p>
          <h2 className="mt-5 max-w-[14ch] font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            {t('The first 50 founding households.')}
          </h2>
          <p className="mt-6 max-w-xl text-lg leading-8 text-muted-foreground">
            {t(
              'We onboard founding households personally, because the system must learn how real homes operate — not how software teams imagine they operate. That is why the cohort is small, and why it is first.',
            )}
          </p>
          <ul className="mt-9 space-y-3.5">
            {[
              t('Personal household setup, done with you'),
              t('White-glove migration from your current WhatsApp routines'),
              t('Direct influence on what gets built next'),
              t('Priority access to new operational-intelligence capabilities'),
              t('A direct feedback channel to the founder'),
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-[15px] font-semibold leading-6">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-wealth/10 text-wealth">
                  <Icon icon="ph:check-bold" className="h-3.5 w-3.5" aria-hidden />
                </span>
                {item}
              </li>
            ))}
          </ul>
          <Link
            to="/signup"
            onClick={() => track('cta_click', { location: 'founding_offer', label: 'reserve' })}
            className="mt-10 inline-flex min-h-14 items-center rounded-full bg-primary px-8 text-base font-bold text-primary-foreground shadow-elev-2 transition hover:-translate-y-0.5 hover:shadow-elev-3 active:scale-[0.98]"
          >
            {t('Reserve founding access')}
            <Icon
              icon="ph:arrow-right-bold"
              className="ms-2 h-4 w-4 rtl:-scale-x-100"
              aria-hidden
            />
          </Link>
        </div>
        <div className="relative flex min-h-[380px] flex-col justify-between border-t border-border bg-secondary p-7 sm:p-12 lg:border-s lg:border-t-0 lg:p-16">
          <div>
            {/* Deliberately not a price. docs/product/pricing.md is LOCKED on
                this: public prices are $39 / $99 / from $199 and there is no
                permanent founding-member rate. What the cohort buys is
                attention, and attention is the scarce thing — so that is what
                this panel says. */}
            <p className="text-sm font-bold text-muted-foreground">
              {t('Places in the first cohort')}
            </p>
            <p className="mt-3 font-heading text-6xl font-bold tracking-[-0.05em] sm:text-7xl">
              50
            </p>
            <p className="mt-2 text-base font-semibold text-muted-foreground">
              {t('households onboarded personally, by the founder')}
            </p>
          </div>
          <div className="mt-14 space-y-4">
            {[
              t('Personal onboarding is capacity-limited — that is the real scarcity'),
              t('You pay only when your household goes live'),
              t('No card until launch eligibility is confirmed'),
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 text-sm font-bold">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-card text-wealth shadow-elev-1">
                  <Icon icon="ph:check-bold" className="h-4 w-4" aria-hidden />
                </span>
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Questions ───────────────────────────────────────────────────────────

function Questions() {
  return (
    <section id="questions" className="border-t border-border px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.75fr_1.25fr] lg:gap-24">
        <Reveal>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary">
            {t('Questions')}
          </p>
          <h2 className="mt-5 font-heading text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            {t('Before you bring it home.')}
          </h2>
        </Reveal>
        <div className="border-t border-border">
          <Faq question={t('Who is Famanage for?')}>
            {t(
              'Households with staff — a housekeeper, a driver, a nanny, a cook. If you run your home on your own, you don’t need us.',
            )}
          </Faq>
          <Faq question={t('Is Famanage a smart-home controller?')}>
            {t(
              'No. It is the operating system for the people and work behind your home: tasks, routines, expenses, pay, household knowledge and communication.',
            )}
          </Faq>
          <Faq question={t('How does my household team get access?')}>
            {t(
              'The owner creates each staff account and controls what that person can see. Staff get one big button and a focused Today view — in their own language, right-to-left included.',
            )}
          </Faq>
          <Faq question={t('Does it work offline?')}>
            {t('Yes. The work of the house doesn’t stop when the internet does.')}
          </Faq>
          <Faq question={t('Which languages are supported?')}>
            {t(
              'English, Hebrew, Urdu and Tagalog are built into the product so instructions can reach each person in a language they understand.',
            )}
          </Faq>
          <Faq question={t('What does founding access include?')}>
            {t(
              'Personal setup done with you, white-glove migration from your current WhatsApp routines, and a direct line to the founder. It is not a private price — plans are public and the same for everyone. Requesting access today does not require a card.',
            )}
          </Faq>
          <Faq question={t('What does it cost?')}>
            {t(
              'Free is $0 forever for manual task management. Home is $39 a month for one worker, Household $99 for three, Private Household from $199 for six. Prices are in USD and the same for every customer.',
            )}
          </Faq>
        </div>
      </div>
    </section>
  );
}

function Faq({ question, children }: { question: string; children: ReactNode }) {
  return (
    <details className="group border-b border-border">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-7 text-lg font-bold marker:content-none">
        <span>{question}</span>
        <Icon
          icon="ph:plus-bold"
          className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-open:rotate-45"
          aria-hidden
        />
      </summary>
      <p className="max-w-[42rem] pb-7 pe-10 leading-7 text-muted-foreground">{children}</p>
    </details>
  );
}

// ─── Act VII · The obvious next step — the panel grows to meet you ───────

function FinalCall() {
  const ref = useRef<HTMLDivElement>(null);
  const p = useEntranceProgress(ref);
  return (
    <section className="px-4 pb-6 sm:px-6 lg:px-8">
      <div
        ref={ref}
        className="mx-auto flex min-h-[520px] max-w-[1380px] flex-col items-center justify-center overflow-hidden rounded-card bg-primary px-5 py-20 text-center text-primary-foreground will-change-transform sm:px-10"
        style={{
          transform: `scale(${0.92 + p * 0.08})`,
          opacity: 0.35 + p * 0.65,
        }}
      >
        <img
          src={markOrange}
          alt=""
          className="h-11 w-auto brightness-0 invert"
          draggable={false}
        />
        <h2 className="mt-8 max-w-[14ch] font-heading text-4xl font-bold leading-[0.95] tracking-[-0.045em] sm:text-6xl lg:text-7xl">
          {t('Your home already runs. Let it run itself.')}
        </h2>
        <p className="mt-7 max-w-xl text-lg leading-8 text-primary-foreground/75">
          {t('Join the founding release and help shape the calmest way to run a home.')}
        </p>
        <Link
          to="/signup"
          className="mt-9 inline-flex min-h-14 items-center rounded-full bg-background px-8 text-base font-bold text-foreground shadow-elev-2 transition hover:-translate-y-0.5 hover:shadow-elev-3 active:scale-[0.98]"
        >
          {t('Start your household')}
        </Link>
        <p className="mt-4 text-sm font-medium text-primary-foreground/70">
          {t('Free plan available · no card today')}
        </p>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <MarketingMark />
          <p className="mt-4 max-w-sm text-sm leading-6 text-muted-foreground">
            {t('Freedom from managing your own house.')}
          </p>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm font-semibold text-muted-foreground">
          <a href="#how" className="hover:text-foreground">
            {t('How it works')}
          </a>
          <a href="#questions" className="hover:text-foreground">
            {t('Questions')}
          </a>
          <a href="mailto:hello@famanage.com" className="hover:text-foreground">
            {t('Contact')}
          </a>
          <Link to="/login" className="hover:text-foreground">
            {t('Sign in')}
          </Link>
          <Link to="/staff/login" className="hover:text-foreground">
            {t('Staff?')} {t('Sign in here')}
          </Link>
          <LegalLinks />
        </div>
      </div>
      <div className="mx-auto mt-10 max-w-7xl border-t border-border pt-6 text-xs font-medium text-muted-foreground/70">
        © 2026 Famanage
      </div>
    </footer>
  );
}

// ─── Product visuals — real screens, in honest frames ────────────────────

/** A desktop screenshot inside a browser frame: three dots and the URL.
 *  The frame says "this is the actual app" without a word of copy. */
function BrowserShot({ src, alt }: { src: string; alt: string }) {
  return (
    <figure className="overflow-hidden rounded-card border border-border bg-card shadow-elev-3 transition-transform duration-500 hover:scale-[1.01]">
      <div
        className="flex items-center gap-1.5 border-b border-border bg-secondary px-4 py-2.5"
        dir="ltr"
      >
        <span className="h-2.5 w-2.5 rounded-full bg-border" />
        <span className="h-2.5 w-2.5 rounded-full bg-border" />
        <span className="h-2.5 w-2.5 rounded-full bg-border" />
        <span className="mx-auto flex w-56 items-center justify-center gap-1.5 rounded-full bg-card px-3 py-1 text-[10px] font-semibold text-muted-foreground">
          <Icon icon="ph:lock-bold" className="h-3 w-3" aria-hidden />
          famanage.com
        </span>
      </div>
      <img src={src} alt={alt} className="w-full" loading="lazy" draggable={false} />
    </figure>
  );
}

/** The staff app, framed like the phone it actually runs on. */
function PhoneShot({ src, alt }: { src: string; alt: string }) {
  return (
    <figure className="mx-auto max-w-[340px] overflow-hidden rounded-[3rem] border border-border bg-foreground p-2.5 shadow-elev-3 transition-transform duration-500 hover:scale-[1.02]">
      <div className="overflow-hidden rounded-[2.4rem] bg-background">
        <img src={src} alt={alt} className="w-full" loading="lazy" draggable={false} />
      </div>
    </figure>
  );
}

/** The name, set in type. */
export function Wordmark({
  className = '',
  compact = false,
}: { className?: string; compact?: boolean }) {
  return (
    <div className={`flex items-center ${className}`} dir="ltr">
      <span
        className={
          compact
            ? 'text-base font-bold tracking-tight text-foreground'
            : 'text-3xl font-bold tracking-tight text-foreground'
        }
      >
        famanage
      </span>
    </div>
  );
}

const DESTINATION_ICON: Record<BrandIconName, string> = {
  home: 'ph:house-fill',
  tasks: 'ph:check-square-bold',
  quick: 'ph:sparkle-fill',
  staff: 'ph:users-three-bold',
  routines: 'ph:clock-counter-clockwise-bold',
  money: 'ph:currency-circle-dollar-bold',
  memory: 'ph:book-open-bold',
  profile: 'ph:user-bold',
};

export function BrandIcon({ name, className = '' }: { name: BrandIconName; className?: string }) {
  return <Icon icon={DESTINATION_ICON[name]} className={className} aria-hidden />;
}

const MAJOR_VERSION = 'v1';
export function BuildChip({ className = '' }: { className?: string }) {
  const id = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';
  return (
    <span
      title={`${MAJOR_VERSION} · ${id}`}
      className={`select-none rounded bg-secondary px-1.5 py-0.5 font-mono text-[10px] leading-none text-muted-foreground ${className}`}
      dir="ltr"
    >
      {MAJOR_VERSION} · {id}
    </span>
  );
}
